import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Appointment, CreateAppointmentDtoSchema } from "@myapp/contract";
import { AppointmentEditProvider } from "../../../features/appointment-edit";
import { AppointmentInteractionProvider } from "../../../features/appointment-block-interaction";
import { DispositionConfigProvider } from "../../../features/disposition";
import PageLayout from "../../../shared/ui/layouts/PageLayout/PageLayout";
import { TimeLineWidget, TimeLineWeekWidget, TimelineNav } from "../../../widgets/timeline";
import { DraftAppointmentPanel, DraftTaskPanel, useDispositionDraftStore } from "../../../features/disposition";
import type { DefaultDurations } from "../../../features/disposition";
import { useTimelineView } from "../../../features/timeline-navigation";
import { isMidnightScheduledDate } from "../../../shared/lib/timeline/dateHelpers";
import { parseAsInteger, parseAsString, useQueryState } from "nuqs";
import { appointmentApi, useCreateAppointment, useUpdateAppointment } from "../../../entities/appointment";
import { useNavigate } from "react-router-dom";
import { routes } from "src/shared/config/routes";
import { TASK_COLORS } from "../../../shared/lib/timelineColors";
import Modal from "../../../shared/ui/overlay/Modal/Modal";
import Select from "../../../shared/ui/controls/Select/Select";
import { ISelectOption } from "../../../shared/types/ISelectOption";
import { useTaskDetail, useUpdateTask } from "../../../entities/task";
import { useWorkOrderDetail } from "../../../entities/work-order";
import { parseStundenToMinutes } from "../../../shared/helpers/duration";
import { getErrorMessage } from "../../../shared/lib/errors/getErrorMessage";

const BASE_DURATIONS: DefaultDurations = {
  plannedTravelDuration: 30,
  plannedWorkDuration: 60,
  plannedDepartureDuration: 30,
};

const RESCHEDULE_REASON_OPTIONS: ISelectOption[] = [
  { value: "MIETER_VERURSACHT", label: "Vom Mieter verursacht" },
  { value: "TECHNIKER_VERHINDERT", label: "Techniker verhindert" },
  { value: "MATERIAL_FEHLT", label: "Material nicht verfügbar" },
  { value: "TERMIN_KONFLIKT", label: "Terminkonflikt" },
  { value: "ANDERE_URSACHE", label: "Andere Ursache" },
];

export const DispositionPage = () => {
  const navigate = useNavigate();
  const [workOrderId] = useQueryState("workOrderId", parseAsString.withDefault(""));
  const [appointmentId, setAppointmentId] = useQueryState("appointmentId", parseAsString.withDefault(""));
  const [gewerk] = useQueryState("gewerk", parseAsInteger.withDefault(0));
  const [taskId, setTaskId] = useQueryState("taskId", parseAsInteger.withDefault(0));

  const { data: workOrderData } = useWorkOrderDetail(workOrderId || undefined);
  const defaultDurations = useMemo<DefaultDurations>(() => {
    const parsed = parseStundenToMinutes(workOrderData?.external.infoText);
    return parsed ? { ...BASE_DURATIONS, plannedWorkDuration: parsed } : BASE_DURATIONS;
  }, [workOrderData?.external.infoText]);

  const { view, setView } = useTimelineView();
  const [, setDayKey] = useQueryState("day", parseAsString.withDefault(""));

  const [files, setFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<string[]>([]);
  const { mutateAsync: createAppointment, isPending: isCreatePending } = useCreateAppointment();
  const { mutateAsync: updateAppointment, isPending: isUpdatePending } = useUpdateAppointment();

  const [backendError, setBackendError] = useState<string | null>(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState<string | number | null>(null);
  const [editingAppointmentStatus, setEditingAppointmentStatus] = useState<Appointment['status'] | null>(null);
  const [rescheduleCustomReason, setRescheduleCustomReason] = useState("");

  const draft = useDispositionDraftStore((s) => s.draft);
  const technicianName = useDispositionDraftStore((s) => s.technicianName);
  const finalizeDraft = useDispositionDraftStore((s) => s.finalizeDraft);
  const setGhost = useDispositionDraftStore((s) => s.setGhost);
  const setDurations = useDispositionDraftStore((s) => s.setDurations);
  const clearAll = useDispositionDraftStore((s) => s.clearAll);
  const isGhostInvalid = useDispositionDraftStore((s) => s.isGhostInvalid);
  const isFullDay = useDispositionDraftStore((s) => s.isFullDay);

  // ── Task inline-edit mode ──────────────────────────────────────────────────
  const isTaskMode = taskId > 0;
  const { data: selectedTask } = useTaskDetail(taskId);
  const updateTask = useUpdateTask(taskId);
  const autoGhostRef = useRef<number | null>(null);

  // Auto-create committed ghost when a task is selected (mirrors TaskPlanPage logic)
  useEffect(() => {
    if (!isTaskMode || !selectedTask) return;
    if (!selectedTask.plannedStart || !selectedTask.assignedTechnician) return;
    if (isMidnightScheduledDate(selectedTask.plannedStart)) return;
    if (autoGhostRef.current === selectedTask.id) return;
    autoGhostRef.current = selectedTask.id;

    const workStart = new Date(selectedTask.plannedStart);
    const anfahrtStartIso = new Date(
      workStart.getTime() - selectedTask.plannedTravelDuration * 60_000,
    ).toISOString();

    const durations: DefaultDurations = {
      plannedTravelDuration: selectedTask.plannedTravelDuration || BASE_DURATIONS.plannedTravelDuration,
      plannedWorkDuration: selectedTask.plannedWorkDuration || BASE_DURATIONS.plannedWorkDuration,
      plannedDepartureDuration: selectedTask.plannedDepartureDuration || BASE_DURATIONS.plannedDepartureDuration,
    };

    // Navigate timeline to the task's scheduled day
    const d = new Date(selectedTask.plannedStart);
    setDayKey(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
    );

    setDurations(durations);
    setGhost({ technicianId: String(selectedTask.assignedTechnician.id), anfahrtStartIso, committed: true });
    finalizeDraft({
      technicianId: String(selectedTask.assignedTechnician.id),
      technicianName: selectedTask.assignedTechnician.fullName ?? null,
      anfahrtStartIso,
      durations,
      workOrderId: "",
      tradeCode: 0,
    });
  }, [selectedTask, isTaskMode, setDayKey, setDurations, setGhost, finalizeDraft]);

  // ── Appointment edit mode ──────────────────────────────────────────────────
  // Clear stale store state on unmount so next visit starts clean.
  // Must NOT clear on mount — when React Query cache is warm, child effects
  // (useGhostBlock) fire before parent effects (child-first order), so clearing
  // on mount would wipe description that was just loaded by handleEditingAppointmentLoaded.
  useEffect(() => {
    return () => { clearAll(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isEditMode = !!appointmentId;
  const isPending = isCreatePending || isUpdatePending;

  const handleEditingAppointmentLoaded = useCallback((appointment: Appointment) => {
    const anfahrtStartIso = new Date(
      new Date(appointment.plannedStart).getTime() - appointment.plannedTravelDuration * 60_000
    ).toISOString();

    finalizeDraft({
      technicianId: String(appointment.assignedTechnicianId),
      technicianName: appointment.technician?.name ?? null,
      anfahrtStartIso,
      durations: {
        plannedTravelDuration: appointment.plannedTravelDuration,
        plannedWorkDuration: appointment.plannedWorkDuration,
        plannedDepartureDuration: appointment.plannedDepartureDuration,
      },
      workOrderId: appointment.workOrderId,
      tradeCode: appointment.tradeCode,
      description: appointment.description ?? null,
      isFullDay: appointment.isFullDay,
    });

    setExistingAttachments(appointment.attachments ?? []);
    setFiles([]);
    setEditingAppointmentStatus(appointment.status);
  }, [finalizeDraft]);

  const handleDraftFinalized = (
    technicianId: string,
    technicianName: string | null,
    anfahrtStartIso: string,
    durations: DefaultDurations,
    tradeCode?: number,
  ) => {
    const currentDraft = useDispositionDraftStore.getState().draft;
    const currentDescription = currentDraft?.description ?? null;
    const currentWorkOrderId = currentDraft?.workOrderId || workOrderId || "";
    finalizeDraft({
      technicianId,
      technicianName,
      anfahrtStartIso,
      durations,
      workOrderId: currentWorkOrderId,
      tradeCode: tradeCode ?? gewerk,
      description: currentDescription,
    });

    // Keep URL day param in sync with the committed appointment date
    const d = new Date(anfahrtStartIso);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    setDayKey(key);
  };

  // ── Save / cancel handlers ─────────────────────────────────────────────────
  const handleSaveTask = async () => {
    const currentDraft = useDispositionDraftStore.getState().draft;
    if (!currentDraft) return;
    if (isGhostInvalid) return;

    const workStartIso = new Date(
      new Date(currentDraft.date).getTime() + currentDraft.plannedTravelDuration * 60_000,
    ).toISOString();

    await updateTask.mutateAsync({
      plannedStart: workStartIso,
      assignedTechnicianId: currentDraft.assignedTechnicianId,
      plannedTravelDuration: currentDraft.plannedTravelDuration,
      plannedWorkDuration: currentDraft.plannedWorkDuration,
      plannedDepartureDuration: currentDraft.plannedDepartureDuration,
    });

    clearAll();
    autoGhostRef.current = null;
    setTaskId(0);
  };

  const handleCancelTask = () => {
    clearAll();
    autoGhostRef.current = null;
    setTaskId(0);
  };

  const handleSave = async () => {
    const currentDraft = useDispositionDraftStore.getState().draft;
    if (!currentDraft) return;
    if (isGhostInvalid) return;

    const isCompletedEdit =
      isEditMode &&
      (editingAppointmentStatus === 'COMPLETED' || editingAppointmentStatus === 'COMPLETED_WITH_FOLLOW_UP');

    if (isCompletedEdit) {
      try {
        setBackendError(null);
        let uploadedPaths: string[] = [];
        if (files.length > 0) {
          const { data } = await appointmentApi.uploadFiles(files);
          uploadedPaths = data;
        }
        const allAttachments = [...existingAttachments, ...uploadedPaths];

        const workStartIso = new Date(
          new Date(currentDraft.date).getTime() + currentDraft.plannedTravelDuration * 60_000
        ).toISOString();

        await updateAppointment({
          id: appointmentId,
          dto: {
            assignedTechnicianId: currentDraft.assignedTechnicianId,
            plannedStart: workStartIso,
            tradeCode: currentDraft.tradeCode,
            plannedTravelDuration: currentDraft.plannedTravelDuration,
            plannedWorkDuration: currentDraft.plannedWorkDuration,
            plannedDepartureDuration: currentDraft.plannedDepartureDuration,
            attachments: allAttachments.length > 0 ? allAttachments : null,
            actualStart: workStartIso,
            actualWorkDuration: currentDraft.plannedWorkDuration,
            actualTravelDuration: currentDraft.plannedTravelDuration,
            actualDepartureDuration: currentDraft.plannedDepartureDuration,
            description: currentDraft.description,
          },
        });

        clearAll();
        setFiles([]);
        setExistingAttachments([]);
        setEditingAppointmentStatus(null);
        setAppointmentId("");
      } catch (err) {
        setBackendError(getErrorMessage(err));
      }
      return;
    }

    if (isEditMode) {
      setShowReasonModal(true);
      return;
    }

    try {
      setBackendError(null);
      let uploadedPaths: string[] = [];
      if (files.length > 0) {
        const { data } = await appointmentApi.uploadFiles(files);
        uploadedPaths = data;
      }
      const allAttachments = [...existingAttachments, ...uploadedPaths];

      const workStartIso = new Date(
        new Date(currentDraft.date).getTime() + currentDraft.plannedTravelDuration * 60_000
      ).toISOString();

      const draftWithWorkStart = {
        ...currentDraft,
        plannedStart: workStartIso,
        attachments: allAttachments.length > 0 ? allAttachments : null,
        description: currentDraft.description,
        isFullDay,
      };
      const parsed = CreateAppointmentDtoSchema.safeParse(draftWithWorkStart);
      if (!parsed.success) return;
      await createAppointment({ ...parsed.data, description: currentDraft.description ?? undefined });
      clearAll();
      setFiles([]);
      setExistingAttachments([]);
      navigate(workOrderId ? routes.task.detail(workOrderId) : routes.task.root);
    } catch (err) {
      setBackendError(getErrorMessage(err));
    }
  };

  const handleConfirmReschedule = async () => {
    const currentDraft = useDispositionDraftStore.getState().draft;
    if (!currentDraft || !rescheduleReason) return;
    if (isGhostInvalid) return;

    const reasonLabel = RESCHEDULE_REASON_OPTIONS.find((o) => o.value === rescheduleReason)?.label ?? "";
    const followUpReason =
      rescheduleReason === "ANDERE_URSACHE"
        ? `Andere Ursache: ${rescheduleCustomReason}`
        : reasonLabel;

    try {
      setBackendError(null);
      let uploadedPaths: string[] = [];
      if (files.length > 0) {
        const { data } = await appointmentApi.uploadFiles(files);
        uploadedPaths = data;
      }
      const allAttachments = [...existingAttachments, ...uploadedPaths];

      const workStartIso = new Date(
        new Date(currentDraft.date).getTime() + currentDraft.plannedTravelDuration * 60_000
      ).toISOString();

      await updateAppointment({
        id: appointmentId,
        dto: {
          assignedTechnicianId: currentDraft.assignedTechnicianId,
          plannedStart: workStartIso,
          tradeCode: currentDraft.tradeCode,
          plannedTravelDuration: currentDraft.plannedTravelDuration,
          plannedWorkDuration: currentDraft.plannedWorkDuration,
          plannedDepartureDuration: currentDraft.plannedDepartureDuration,
          attachments: allAttachments.length > 0 ? allAttachments : null,
          followUpReason,
          description: currentDraft.description,
          isFullDay,
        },
      });

      clearAll();
      setFiles([]);
      setExistingAttachments([]);
      setShowReasonModal(false);
      setRescheduleReason(null);
      setRescheduleCustomReason("");
      setAppointmentId("");
    } catch (err) {
      setBackendError(getErrorMessage(err));
    }
  };

  const handleCancel = () => {
    clearAll();
    setFiles([]);
    setExistingAttachments([]);
    setBackendError(null);
    setShowReasonModal(false);
    setRescheduleReason(null);
    setRescheduleCustomReason("");
    setEditingAppointmentStatus(null);
    if (isEditMode) {
      setAppointmentId("");
    }
  };

  const handleTaskSelect = (id: number) => {
    // Mutual exclusion: clear appointment mode when selecting a task
    setAppointmentId("");
    clearAll();
    autoGhostRef.current = null;
    setTaskId(id);
  };

  const handleDayClick = (dayKey: string) => {
    const d = new Date(dayKey + "T00:00:00");
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    setDayKey(`${y}-${m}-${dd}`);
    setView("day");
  };

  const pageTitle = isTaskMode
    ? "Aufgabe umdisponieren"
    : workOrderId
    ? `Termin planen für Auftrag ${workOrderId}`
    : appointmentId
    ? "Termin umdisponieren"
    : "Disposition und Zeitplanung";

  return (
    <PageLayout
      pageTitle={pageTitle}
      headerChildren={<TimelineNav />}
    >
      {view === "day" ? (
        <DispositionConfigProvider value={{
          workOrderId: workOrderId || '',
          defaultDurations,
          ghostColor: isTaskMode ? TASK_COLORS.work : undefined,
          onDraftFinalized: handleDraftFinalized,
        }}>
          <AppointmentEditProvider value={{
            appointmentId: appointmentId || undefined,
            onEditingAppointmentLoaded: handleEditingAppointmentLoaded,
          }}>
            <AppointmentInteractionProvider value={{
              onAppointmentSelect: (id) => { setTaskId(0); setAppointmentId(id); },
            }}>
              <TimeLineWidget
                onTaskSelect={handleTaskSelect}
                editingTaskId={isTaskMode ? taskId : undefined}
              />
            </AppointmentInteractionProvider>
          </AppointmentEditProvider>
        </DispositionConfigProvider>
      ) : (
        <TimeLineWeekWidget onDayClick={handleDayClick} />
      )}

      {draft && isTaskMode && selectedTask && (
        <DraftTaskPanel
          taskTitle={selectedTask.title}
          taskId={taskId}
          onSave={handleSaveTask}
          onCancel={handleCancelTask}
          isPending={updateTask.isPending}
        />
      )}

      {draft && !isTaskMode && (
        <DraftAppointmentPanel
          onSave={handleSave}
          onCancel={handleCancel}
          isPending={isPending}
          mode={isEditMode ? "edit" : "create"}
          files={files}
          onFilesChange={setFiles}
          existingAttachments={existingAttachments}
          onRemoveExistingAttachment={(i) =>
            setExistingAttachments((prev) => prev.filter((_, idx) => idx !== i))
          }
          backendError={backendError}
        />
      )}

      <Modal
        show={showReasonModal}
        setShow={setShowReasonModal}
        title="Grund der Umdisponierung"
        submitButton={{ onSubmit: handleConfirmReschedule, title: 'Bestätigen', isPending: isUpdatePending, disabled: !rescheduleReason }}
        onClose={() => {
          setRescheduleReason(null);
          setRescheduleCustomReason("");
        }}
        className="min-h-[200px] !overflow-visible"
      >
        <div className="flex flex-col gap-4 min-w-[320px]">
          <Select
            options={RESCHEDULE_REASON_OPTIONS}
            value={rescheduleReason}
            onChange={setRescheduleReason}
            placeholder="Bitte Grund angeben"
          />
          {rescheduleReason === "ANDERE_URSACHE" && (
            <textarea
              className="w-full rounded-[14px] border border-[var(--color-primary)] px-4 py-3 text-sm font-['PT_Sans'] text-[var(--color-primary)] outline-none resize-none"
              rows={3}
              placeholder="Bitte Grund beschreiben..."
              value={rescheduleCustomReason}
              onChange={(e) => setRescheduleCustomReason(e.target.value)}
            />
          )}
        </div>
      </Modal>
    </PageLayout>
  );
};
